import * as React from "react";
import {
  ACTIVE_LEAD_STATUSES,
  APPROVED_STATUS,
  CANCELED_STATUS,
  NO_ANSWER_STATUS,
} from "../lead-status";
import { breakdownTotals, pct, sumCounts } from "../lib/stats-math";
import { formatDuration } from "../lib/lead-fields";

export interface BreakdownRow {
  key: string;
  label: string;
  color: string | null;
  counts: Record<string, number>;
  // 부재 클릭 수(연락시도 누적). 직원별에서만 채워짐.
  attempts?: number;
}

type SortDir = "asc" | "desc";
type ColKind = "text" | "num" | "pct";

interface Column {
  key: string;
  label: string;
  kind: ColKind;
  // 행 → 정렬/합계용 수치(text 컬럼은 label 문자열).
  value: (r: BreakdownRow) => number | string;
}

// 각 컬럼의 표시 문자열. pct 는 value(비율) 를 총계 대비 %로.
function display(col: Column, r: BreakdownRow): string {
  const v = col.value(r);
  if (col.kind === "text") return String(v);
  if (col.kind === "num") return (v as number).toLocaleString();
  return pct(v as number, sumCounts(r.counts)); // pct: value=해당건수, 분모=상담건수
}

// 직원별/매체별 처리현황 표. 모든 상태 + 전환율/취소율/부재율, 컬럼 클릭 정렬.
export function StatsBreakdownTable({
  title,
  firstColHeader,
  rows,
  showColorDot,
  showAttempts,
}: {
  title: string;
  firstColHeader: string;
  rows: BreakdownRow[];
  showColorDot?: boolean;
  // 부재 클릭 수(연락시도) 컬럼 노출 — 직원별에서만.
  showAttempts?: boolean;
}): React.ReactElement {
  const columns = React.useMemo<Column[]>(() => {
    const cols: Column[] = [
      { key: "label", label: firstColHeader, kind: "text", value: (r) => r.label },
      { key: "total", label: "상담건수", kind: "num", value: (r) => sumCounts(r.counts) },
    ];
    for (const s of ACTIVE_LEAD_STATUSES)
      cols.push({
        key: `st:${s.value}`,
        label: s.label,
        kind: "num",
        value: (r) => r.counts[s.value] ?? 0,
      });
    if (showAttempts)
      cols.push({
        key: "attempts",
        label: "부재클릭수",
        kind: "num",
        value: (r) => r.attempts ?? 0,
      });
    cols.push(
      { key: "conv", label: "전환율", kind: "pct", value: (r) => r.counts[APPROVED_STATUS] ?? 0 },
      { key: "cancel", label: "취소율", kind: "pct", value: (r) => r.counts[CANCELED_STATUS] ?? 0 },
      { key: "absent", label: "부재율", kind: "pct", value: (r) => r.counts[NO_ANSWER_STATUS] ?? 0 },
    );
    return cols;
  }, [firstColHeader, showAttempts]);

  const [sort, setSort] = React.useState<{ key: string; dir: SortDir } | null>({
    key: "total",
    dir: "desc",
  });

  const sortedRows = React.useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const factor = sort.dir === "asc" ? 1 : -1;
    // pct 는 비율(건수/상담건수)로 정렬.
    const metric = (r: BreakdownRow): number | string => {
      if (col.kind === "pct") {
        const total = sumCounts(r.counts);
        return total > 0 ? (col.value(r) as number) / total : 0;
      }
      return col.value(r);
    };
    return [...rows].sort((a, b) => {
      const av = metric(a);
      const bv = metric(b);
      if (typeof av === "string" || typeof bv === "string")
        return String(av).localeCompare(String(bv), "ko") * factor;
      return (av - bv) * factor;
    });
  }, [rows, sort, columns]);

  // 합계 행 — 상태별 합을 구해 num/pct 컬럼을 채움.
  const totals = React.useMemo(() => breakdownTotals(rows), [rows]);

  const totalCell = (col: Column): string => {
    switch (col.kind) {
      case "text":
        return "합계";
      case "pct": {
        // conv/cancel/absent 각 상태합 ÷ 상담건수합.
        const st =
          col.key === "conv" ? APPROVED_STATUS : col.key === "cancel" ? CANCELED_STATUS : NO_ANSWER_STATUS;
        return pct(totals.statusSum[st] ?? 0, totals.total);
      }
      default: {
        if (col.key === "total") return totals.total.toLocaleString();
        if (col.key === "attempts") return totals.attempts.toLocaleString();
        if (col.key.startsWith("st:"))
          return (totals.statusSum[col.key.slice(3)] ?? 0).toLocaleString();
        return "";
      }
    }
  };

  const toggle = (key: string) =>
    setSort((prev) => {
      if (prev?.key !== key) return { key, dir: "desc" };
      if (prev.dir === "desc") return { key, dir: "asc" };
      return null;
    });

  return (
    <div>
      <h3 className="text-[13px] font-semibold tracking-tight mb-2">{title}</h3>
      <div className="rounded-lg border overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="bg-canvas text-ink-subtle text-left">
              {columns.map((col) => (
                <SortableTh
                  key={col.key}
                  label={col.label}
                  align={col.kind === "text" ? undefined : "right"}
                  active={sort?.key === col.key ? sort.dir : null}
                  onClick={() => toggle(col.key)}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-6 text-center text-ink-subtle"
                >
                  데이터가 없습니다.
                </td>
              </tr>
            ) : (
              sortedRows.map((r) => (
                <tr key={r.key} className="border-t border-border">
                  {columns.map((col) =>
                    col.kind === "text" ? (
                      <td key={col.key} className="px-3 py-2 text-ink">
                        <span className="inline-flex items-center gap-1.5">
                          {showColorDot && (
                            <span
                              className="w-2.5 h-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: r.color ?? "#9ca3af" }}
                              aria-hidden
                            />
                          )}
                          <span className="truncate">{r.label}</span>
                        </span>
                      </td>
                    ) : (
                      <td
                        key={col.key}
                        className="px-3 py-2 text-right tabular-nums text-ink whitespace-nowrap"
                      >
                        {display(col, r)}
                      </td>
                    ),
                  )}
                </tr>
              ))
            )}
          </tbody>
          {sortedRows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-border-strong bg-canvas font-semibold">
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-3 py-2 text-ink whitespace-nowrap ${
                      col.kind === "text" ? "" : "text-right tabular-nums"
                    }`}
                  >
                    {totalCell(col)}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function SortableTh({
  label,
  align,
  active,
  onClick,
}: {
  label: string;
  align?: "right";
  active: SortDir | null;
  onClick: () => void;
}): React.ReactElement {
  return (
    <th className={`px-3 py-2 font-medium ${align === "right" ? "text-right" : ""}`}>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-ink whitespace-nowrap ${
          active ? "text-ink" : ""
        }`}
      >
        <span>{label}</span>
        <span
          className="inline-flex flex-col gap-[0.2px] leading-none text-[7px] shrink-0"
          aria-hidden
        >
          <span className={active === "asc" ? "text-ink" : "text-ink-subtle/40"}>
            ▲
          </span>
          <span className={active === "desc" ? "text-ink" : "text-ink-subtle/40"}>
            ▼
          </span>
        </span>
      </button>
    </th>
  );
}

// 전체 요약 — 전체 리드 대비 승인 전환율/취소율.
export function StatsSummary({
  rows,
  avgContactSeconds,
  contactSampleCount,
}: {
  rows: BreakdownRow[];
  // 영업시간 내 인입 리드 평균 컨택시간(초). undefined 면 타일 미표시.
  avgContactSeconds?: number | null;
  contactSampleCount?: number;
}): React.ReactElement {
  const { total, approved, canceled } = React.useMemo(() => {
    let total = 0;
    let approved = 0;
    let canceled = 0;
    for (const r of rows) {
      total += sumCounts(r.counts);
      approved += r.counts[APPROVED_STATUS] ?? 0;
      canceled += r.counts[CANCELED_STATUS] ?? 0;
    }
    return { total, approved, canceled };
  }, [rows]);

  return (
    <div className="flex items-stretch gap-3 flex-wrap">
      <SummaryTile label="전체 상담" value={total.toLocaleString()} />
      <SummaryTile
        label="승인"
        value={approved.toLocaleString()}
        sub={`전환율 ${pct(approved, total)}`}
        tone="success"
      />
      <SummaryTile
        label="취소"
        value={canceled.toLocaleString()}
        sub={`취소율 ${pct(canceled, total)}`}
        tone="danger"
      />
      {avgContactSeconds !== undefined && (
        <SummaryTile
          label="평균 컨택시간"
          value={formatDuration(avgContactSeconds)}
          sub={`영업시간 인입 ${(contactSampleCount ?? 0).toLocaleString()}건`}
        />
      )}
    </div>
  );
}

function SummaryTile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "success" | "danger";
}): React.ReactElement {
  return (
    <div className="rounded-lg border px-4 py-3 min-w-[130px]">
      <div className="text-[11px] text-ink-subtle mb-0.5">{label}</div>
      <div className="text-[20px] font-semibold tabular-nums leading-tight text-ink">
        {value}
      </div>
      {sub && (
        <div
          className="text-[11px] mt-0.5 tabular-nums"
          style={{
            color:
              tone === "success"
                ? "#16a34a"
                : tone === "danger"
                  ? "#dc2626"
                  : undefined,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}
